// Exclusively matches with email addresses separated by commas & optional whitespace
export const EMAIL_REGEX =
  /^([\w-+.]+@([\w-]+\.)+[\w-]{1,63})(,\s*[\w-+.]+@([\w-]+\.)+[\w-]{1,63})*$/;

// Matches with email addresses from any string, doesn't specify comma or whitespace formatting
export const EMAIL_REGEX_GLOBAL =
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

export class NoEmailToRecipientError extends Error {
  override name = "NoEmailToRecipientError";

  constructor() {
    super("At least one 'to' recipient is required to create a mailto link");
  }
}

export function emailIsValid(email: string): boolean {
  return EMAIL_REGEX.test(email.toLowerCase());
}

export function extractEmail(string: string): string {
  const match: string[] | null = string.match(EMAIL_REGEX);
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return match ? match[0] : "";
}

/**
 * Extracts email addresses from a string using regex
 * @param text - Text potentially containing email addresses
 * @returns Array of unique email addresses found
 */
export function extractMultipleEmailAddresses(text: string): string[] {
  if (!text) return [];

  // RFC 5322 compliant email regex (simplified for common cases)
  const emailRegex = EMAIL_REGEX_GLOBAL;

  const matches = text.match(emailRegex) || [];

  return [...new Set(matches)];
}

/**
 * Creates a secure mailto link with validated email addresses and encoded parameters
 * @param params - Object containing mailto parameters
 * @param params.to - Required array of recipient email addresses
 * @param params.subject - Optional email subject
 * @param params.body - Optional email body
 * @returns A properly formatted and encoded mailto link
 * @throws {NoEmailToRecipientError} If no valid 'to' recipients are provided
 * @example
 * // With "to" addresses
 * createMailtoLink({
 *   to: ['support@example.com'],
 *   subject: 'Help Request',
 *   body: 'I need assistance with...'
 * })
 * // Returns: "mailto:support@example.com?subject=Help%20Request&body=I%20need%20assistance%20with..."
 */
export function createMailtoLink({
  to,
  subject,
  body,
}: {
  to: string[];
  subject?: string;
  body?: string;
}): string {
  // Validate and sanitize email addresses
  const validatedTo = to
    .filter((email): email is string => typeof email === "string")
    .map((email) => email.trim())
    .filter((email) => email !== "" && emailIsValid(email));

  // At least one recipient is required
  if (validatedTo.length === 0) {
    throw new NoEmailToRecipientError();
  }

  const queryParams: string[] = [];

  const mailtoLink = `mailto:${validatedTo.join(",")}`;

  if (subject) {
    // Limit subject length to RFC 2822 max (998 chars) and prevent abuse
    // In practice, subjects are typically 30-70 chars, but we allow up to RFC limit
    const shortenedSubject = subject.substring(0, 500);
    queryParams.push(`subject=${encodeURIComponent(shortenedSubject)}`);
  }

  if (body) {
    // Limit body length to prevent browser URL length issues and abuse
    // Modern browsers typically support URLs up to 8k-32k chars; should rarely be an issue, but just in case since this check is cheap
    const shortenedBody = body.substring(0, 10_000);
    queryParams.push(`body=${encodeURIComponent(shortenedBody)}`);
  }

  if (queryParams.length > 0) {
    return `${mailtoLink}?${queryParams.join("&")}`;
  }

  return mailtoLink;
}

/**
 * Converts email addresses in text to clickable HTML mailto links
 * @param text - The text potentially containing email addresses
 * @returns HTML text with email addresses converted to clickable links
 * @example
 * convertEmailsToMailtoLinks("Contact us at support@example.com or sales@example.com")
 * // Returns: 'Contact us at <a href="mailto:support@example.com">support@example.com</a> or <a href="mailto:sales@example.com">sales@example.com</a>'
 */
export function convertEmailsToMailtoLinks(text: string): string {
  if (!text) return text;

  const emails = extractMultipleEmailAddresses(text);
  if (emails.length === 0) return text;

  let result = text;

  const sortedEmails = [...emails].sort((a, b) => b.length - a.length);

  for (const email of sortedEmails) {
    try {
      const mailtoLink = createMailtoLink({ to: [email] });
      const htmlLink = `<a href="${mailtoLink}" style="color: inherit; text-decoration: underline;">${email}</a>`;

      // Use a more specific regex that doesn't match inside HTML tags
      const emailRegex = new RegExp(
        // handle email addresses that contain regex metacharacters by escaping them with backslash
        `(?<!<[^>]*)\\b${email.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b(?![^<]*>)`,
        "g",
      );
      result = result.replace(emailRegex, htmlLink);
    } catch (error) {
      // Skip invalid emails silently
      if (error instanceof NoEmailToRecipientError) {
        continue;
      }
      throw error;
    }
  }

  return result;
}
